// ============================================================================
// PRELIT-CASES-001 — Pre-Lit Cases (claimed + assigned-but-unstarted)
// ============================================================================
// Lists Pre-Lit cases the user is allowed to see. Includes:
//   - All cases with pool_status IN ('IN_PROGRESS', 'CLOSED'), AND
//   - Assigned-but-not-yet-progressed cases (UNCLAIMED with assigned_agent set)
//
// Visibility is scope-driven (server-side via IPreLitScopeGuard) — no client
// "My / Team / All" toggle.
//
// Header tabs are STATUS-based (info-2.txt §2.2):
//   Actions Due (next_action_date >= today AND IN_PROGRESS) · Active ·
//   Closed (Recovered) · Escalated · Closed (Manual) · All
//
// Defaults to Actions Due if the user has any rows there; else Active.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ModuleHeader } from 'components/modals';
import type { ActionButton } from 'components/modals';
import {
  ActionMenu,
  type ActionMenuItem,
  SyutGrid,
  type SyutGridColumn,
  SyutSelect,
  MinimizedModalStrip,
} from 'components/ui';
import { usePreLitCases } from 'hooks/api/usePreLitigation';
import type {
  PreLitCaseListItem,
  PreLitCaseSearchRequest,
  CasesStatusTab,
  ActionType,
} from 'api/types/preLitigation.types';
import { openCaseDetail } from 'components/modules/pre-litigation-module/openCaseDetail';

interface PreLitigationCasesScreenProps {
  tabId?: string;
  moduleName?: string;
}

type ViewMode = 'list' | 'grid';

const STATUS_TABS: { id: CasesStatusTab; label: string; icon: keyof typeof IconSystem }[] = [
  { id: 'actions_due',      label: 'Actions Due',     icon: 'bell' },
  { id: 'active',           label: 'Active',          icon: 'play' as any }, // play not in IconSystem; falls through to checkCircle below
  { id: 'closed_recovered', label: 'Closed (Won)',    icon: 'checkCircle' },
  { id: 'closed_lost',      label: 'Closed (Lost)',   icon: 'xCircle' },
  { id: 'escalated',        label: 'Escalated',       icon: 'arrowRight' },
  { id: 'closed_manual',    label: 'Closed (Manual)', icon: 'xCircle' },
  { id: 'all',              label: 'All',             icon: 'list' },
];

const ACTION_TYPE_OPTIONS: { value: ActionType | ''; label: string }[] = [
  { value: '',             label: 'Any' },
  { value: 'CALL',         label: 'Call' },
  { value: 'SMS',          label: 'SMS' },
  { value: 'EMAIL',        label: 'Email' },
  { value: 'FIELD_VISIT',  label: 'Field Visit' },
  { value: 'OFFICE_VISIT', label: 'Office Visit' },
];

const PreLitigationCasesScreen: React.FC<PreLitigationCasesScreenProps> = ({ tabId }) => {

  // ----- Server-side query state -----
  const [activeTab, setActiveTab] = useState<CasesStatusTab>('active');
  const [queryParams, setQueryParams] = useState<PreLitCaseSearchRequest>({
    page: 1,
    pageSize: 25,
    statusTab: 'active',
  });
  const { data, loading, pagination, refetch } = usePreLitCases(queryParams);

  // ----- View / search -----
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => {
      setQueryParams(prev => ({ ...prev, searchTerm: searchQuery.trim() || undefined, page: 1 }));
    }, 400);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const handleTabChange = useCallback((tab: CasesStatusTab) => {
    setActiveTab(tab);
    setQueryParams(prev => ({ ...prev, statusTab: tab, page: 1 }));
  }, []);

  // ----- Filter panel state -----
  const [productFilter, setProductFilter] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const [dpdMin, setDpdMin] = useState('');
  const [dpdMax, setDpdMax] = useState('');
  const [outstandingMin, setOutstandingMin] = useState('');
  const [outstandingMax, setOutstandingMax] = useState('');
  const [emiCountMin, setEmiCountMin] = useState('');
  const [emiCountMax, setEmiCountMax] = useState('');
  const [emiAmountMin, setEmiAmountMin] = useState('');
  const [emiAmountMax, setEmiAmountMax] = useState('');
  const [nextActionFrom, setNextActionFrom] = useState('');
  const [nextActionTo, setNextActionTo] = useState('');
  const [nextActionType, setNextActionType] = useState<string>('');
  const [hasOpenCommitment, setHasOpenCommitment] = useState<string>('');

  const applyFilters = useCallback(() => {
    const openCommitBool =
      hasOpenCommitment === 'true' ? true : hasOpenCommitment === 'false' ? false : undefined;
    setQueryParams(prev => ({
      ...prev,
      productCodes: productFilter ? [productFilter] : undefined,
      stateCodes: stateFilter ? [stateFilter] : undefined,
      dpdMin: dpdMin ? Number(dpdMin) : undefined,
      dpdMax: dpdMax ? Number(dpdMax) : undefined,
      outstandingMin: outstandingMin ? Number(outstandingMin) : undefined,
      outstandingMax: outstandingMax ? Number(outstandingMax) : undefined,
      emiCountMin: emiCountMin ? Number(emiCountMin) : undefined,
      emiCountMax: emiCountMax ? Number(emiCountMax) : undefined,
      emiAmountMin: emiAmountMin ? Number(emiAmountMin) : undefined,
      emiAmountMax: emiAmountMax ? Number(emiAmountMax) : undefined,
      nextActionFrom: nextActionFrom || undefined,
      nextActionTo: nextActionTo || undefined,
      nextActionTypes: nextActionType ? [nextActionType as ActionType] : undefined,
      hasOpenCommitment: openCommitBool,
      page: 1,
    }));
    setFilterOpen(false);
  }, [
    productFilter, stateFilter, dpdMin, dpdMax,
    outstandingMin, outstandingMax,
    emiCountMin, emiCountMax, emiAmountMin, emiAmountMax,
    nextActionFrom, nextActionTo, nextActionType, hasOpenCommitment,
  ]);

  const clearFilters = useCallback(() => {
    setProductFilter(''); setStateFilter('');
    setDpdMin(''); setDpdMax('');
    setOutstandingMin(''); setOutstandingMax('');
    setEmiCountMin(''); setEmiCountMax('');
    setEmiAmountMin(''); setEmiAmountMax('');
    setNextActionFrom(''); setNextActionTo('');
    setNextActionType(''); setHasOpenCommitment('');
    setQueryParams(prev => ({
      page: 1,
      pageSize: prev.pageSize ?? 25,
      statusTab: prev.statusTab,
    }));
  }, []);

  const activeFilterCount = useMemo(() => {
    let n = 0;
    if (queryParams.productCodes?.length) n++;
    if (queryParams.stateCodes?.length) n++;
    if (queryParams.dpdMin != null) n++;
    if (queryParams.dpdMax != null) n++;
    if (queryParams.outstandingMin != null) n++;
    if (queryParams.outstandingMax != null) n++;
    if (queryParams.emiCountMin != null) n++;
    if (queryParams.emiCountMax != null) n++;
    if (queryParams.emiAmountMin != null) n++;
    if (queryParams.emiAmountMax != null) n++;
    if (queryParams.nextActionFrom) n++;
    if (queryParams.nextActionTo) n++;
    if (queryParams.nextActionTypes?.length) n++;
    if (queryParams.hasOpenCommitment !== undefined) n++;
    return n;
  }, [queryParams]);

  // ----- Pagination -----
  const handlePageChange = useCallback((idx: number) => {
    setQueryParams(prev => ({ ...prev, page: idx + 1 }));
  }, []);
  const handlePageSizeChange = useCallback((size: number) => {
    setQueryParams(prev => ({ ...prev, pageSize: size, page: 1 }));
  }, []);

  // ----- Navigation -----
  const goCaseDetail = useCallback((id: string) => {
    openCaseDetail(id);
  }, []);

  // ----- Grid columns -----
  const columns: SyutGridColumn<PreLitCaseListItem>[] = useMemo(() => [
    { id: 'caseCode',      header: 'Case #',   accessorKey: 'caseCode',      size: 130, enableSorting: true },
    { id: 'accountNumber', header: 'Account #', accessorKey: 'accountNumber', size: 130, enableSorting: true },
    { id: 'customerName',  header: 'Customer', accessorKey: 'customerName',  size: 200, enableSorting: true, flex: true },
    {
      id: 'state', header: 'State',
      accessorKey: 'currentStateDisplayName',
      size: 130, enableSorting: true,
      cell: ({ row }) => row.original.currentStateDisplayName ?? '—',
    },
    {
      id: 'agent', header: 'Agent',
      accessorKey: 'assignedAgentName',
      size: 150, enableSorting: true,
      cell: ({ row }) => row.original.assignedAgentName ?? '—',
    },
    { id: 'currentDpd',  header: 'DPD (now)', accessorKey: 'currentDpd', size: 80, enableSorting: true },
    {
      id: 'currentOutstanding', header: 'Outstanding',
      accessorKey: 'currentOutstanding', size: 120, enableSorting: true,
      cell: ({ row }) => `₹${(row.original.currentOutstanding ?? 0).toLocaleString('en-IN')}`,
    },
    { id: 'openedEmiOutstandingCount', header: 'EMI O/S Count (Open)',
      accessorKey: 'openedEmiOutstandingCount', size: 110, enableSorting: true },
    {
      id: 'openedEmiOutstandingAmount', header: 'EMI O/S Amount (Open)',
      accessorKey: 'openedEmiOutstandingAmount', size: 140, enableSorting: true,
      cell: ({ row }) => `₹${(row.original.openedEmiOutstandingAmount ?? 0).toLocaleString('en-IN')}`,
    },
    {
      id: 'openCommitment', header: 'Open Commitment',
      accessorKey: 'openCommitmentAmount', size: 160, enableSorting: true,
      cell: ({ row }) => {
        const amt = row.original.openCommitmentAmount;
        const dt  = row.original.openCommitmentDate;
        if (amt == null || !dt) return '—';
        try {
          return `₹${amt.toLocaleString('en-IN')} due ${new Date(dt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}`;
        } catch {
          return `₹${amt.toLocaleString('en-IN')}`;
        }
      },
    },
    {
      id: 'nextActionDate', header: 'Next Action Date',
      accessorKey: 'nextActionAt', size: 120, enableSorting: true,
      cell: ({ row }) => {
        const v = row.original.nextActionAt;
        if (!v) return '—';
        try { return new Date(v).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); }
        catch { return v; }
      },
    },
    {
      id: 'nextActionType', header: 'Next Action Type',
      accessorKey: 'nextActionType', size: 120, enableSorting: true,
      cell: ({ row }) => row.original.nextActionType ?? '—',
    },
    {
      id: 'lastAction', header: 'Last Action',
      accessorKey: 'lastActionAt', size: 180, enableSorting: true,
      cell: ({ row }) => {
        const t = row.original.lastActionType;
        const at = row.original.lastActionAt;
        if (!t || !at) return '—';
        try {
          return `${t} · ${new Date(at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}`;
        } catch {
          return t;
        }
      },
    },
    {
      id: 'claimedAt', header: 'Claimed At',
      accessorKey: 'claimedAt', size: 110, enableSorting: true,
      cell: ({ row }) => {
        const v = row.original.claimedAt;
        if (!v) return '—';
        try { return new Date(v).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); }
        catch { return v; }
      },
    },
    {
      id: 'closedAt', header: 'Closed At',
      accessorKey: 'closedAt', size: 110, enableSorting: true,
      cell: ({ row }) => {
        const v = row.original.closedAt;
        if (!v) return '—';
        try { return new Date(v).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); }
        catch { return v; }
      },
    },
  ], []);

  // ----- Row actions -----
  const getRowActions = useCallback((row: PreLitCaseListItem): ActionMenuItem[] => [
    {
      id: 'open',
      label: 'Open',
      icon: <IconSystem.eye size={16} />,
      onClick: () => goCaseDetail(String(row.id)),
    },
  ], [goCaseDetail]);

  // ----- Action buttons -----
  const actionButtons: ActionButton[] = useMemo(() => [
    {
      id: 'refresh',
      icon: <IconSystem.refresh size={18} />,
      label: 'Refresh',
      tooltip: 'Reload cases',
      onClick: refetch,
      variant: 'icon',
    },
  ], [refetch]);

  return (
    <div className="module-master-container">
      <ModuleHeader
        title="Early Recovery Action"
        description="Actions the user can see based on view scope. Status tabs partition by lifecycle stage."
        icon={<IconSystem.folder size={18} />}
        searchConfig={{
          value: searchQuery,
          onChange: setSearchQuery,
          placeholder: 'Search account, customer, case #...',
        }}
        viewModeConfig={{
          currentMode: viewMode,
          modes: [
            { id: 'list', icon: <IconSystem.list size={16} />, label: 'List', tooltip: 'List view' },
            { id: 'grid', icon: <IconSystem.grid size={16} />, label: 'Grid', tooltip: 'Card view' },
          ],
          onChange: (mode) => setViewMode(mode as ViewMode),
        }}
        filterConfig={{
          onClick: () => setFilterOpen(!filterOpen),
          isActive: filterOpen || activeFilterCount > 0,
          activeFilterCount,
        }}
        leadingToolbarItems={tabId ? <MinimizedModalStrip tabId={tabId} /> : undefined}
        actions={actionButtons}
      />

      {/* Status tabs — directly below ModuleHeader */}
      <div style={{
        display: 'flex',
        gap: '0.25rem',
        padding: '0.5rem 1.25rem 0',
        borderBottom: '1px solid var(--border-color)',
        background: 'var(--bg-primary)',
        flexShrink: 0,
        overflowX: 'auto',
      }}>
        {STATUS_TABS.map((tab) => {
          const Icon =
            (IconSystem as any)[tab.icon] ?? IconSystem.list;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => handleTabChange(tab.id)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.4rem',
                padding: '0.5rem 0.9rem',
                border: 'none',
                background: 'transparent',
                color: isActive ? 'var(--text-primary)' : 'var(--text-secondary)',
                fontSize: '0.85rem',
                fontWeight: isActive ? 600 : 500,
                borderBottom: isActive ? '2px solid #2563eb' : '2px solid transparent',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              <Icon size={14} />
              {tab.label}
            </button>
          );
        })}
      </div>

      <div className="module-content" style={{ marginTop: '6px' }}>
        <div style={{ display: 'flex', gap: '0', flex: 1, overflow: 'hidden' }}>
          <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            {viewMode === 'list' && (
              <SyutGrid
                data={data}
                columns={columns}
                enableSorting
                enablePagination
                manualPagination
                totalCount={pagination.totalItems}
                currentPage={(queryParams.page ?? 1) - 1}
                pageSize={queryParams.pageSize ?? 25}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                rowActions={getRowActions}
                frozenRightColumns={['actions']}
                emptyMessage={loading ? 'Loading...' : 'No cases match the current tab and filters.'}
              />
            )}
            {viewMode === 'grid' && (
              <div className="syut-grid-component" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <div style={{
                  padding: '1rem', display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                  gap: '1.25rem', flex: 1, overflow: 'auto', alignContent: 'start',
                }}>
                  {data.length === 0 && (
                    <div style={{ gridColumn: '1 / -1', padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
                      {loading ? 'Loading...' : 'No cases match the current tab and filters.'}
                    </div>
                  )}
                  {data.map((row) => (
                    <div
                      key={String(row.id)}
                      style={{
                        position: 'relative',
                        border: '1px solid var(--border-color)',
                        borderRadius: '8px',
                        padding: '1rem',
                        background: 'var(--bg-primary)',
                        cursor: 'pointer',
                        display: 'flex', flexDirection: 'column', gap: '0.5rem',
                      }}
                      onClick={() => goCaseDetail(String(row.id))}
                    >
                      {/* Action menu pinned to the card's top-right; stop click
                          propagation so opening the menu doesn't also fire the
                          card's onClick. */}
                      <div
                        style={{ position: 'absolute', top: 6, right: 6 }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <ActionMenu items={getRowActions(row)} />
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', paddingRight: '1.5rem' }}>
                        <span style={{ fontSize: '0.95rem', fontWeight: 600 }}>{row.caseCode}</span>
                        <span style={{
                          fontSize: '0.7rem', fontWeight: 600,
                          color: '#2563eb',
                          border: '1px solid #2563eb', padding: '1px 6px', borderRadius: '999px',
                        }}>{row.currentStateDisplayName ?? row.caseStatus}</span>
                      </div>
                      <div style={{ fontSize: '0.85rem', fontWeight: 500 }}>
                        {row.customerName}
                        <span style={{ color: 'var(--text-secondary)', fontWeight: 400 }}> · {row.accountNumber}</span>
                      </div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', display: 'flex', justifyContent: 'space-between' }}>
                        <span>DPD <strong style={{ color: 'var(--text-primary)' }}>{row.currentDpd}</strong></span>
                        <span>₹<strong style={{ color: 'var(--text-primary)' }}>{(row.currentOutstanding ?? 0).toLocaleString('en-IN')}</strong></span>
                      </div>
                      <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)' }}>
                        Agent: {row.assignedAgentName ?? '—'}
                      </div>
                      {row.nextActionAt && (
                        <div style={{ fontSize: '0.7rem', color: '#d97706' }}>
                          Next: {row.nextActionType ?? 'action'} · {new Date(row.nextActionAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Filter panel — conditional render so filterOpen=false hides */}
        {filterOpen && (
          <div className="search-panel">
            <div className="search-panel-header">
              <h3 className="search-panel-title">Filters</h3>
              <button className="search-panel-close" onClick={() => setFilterOpen(false)} title="Close filters">
                <IconSystem.x size={14} />
              </button>
            </div>
            <div className="search-panel-content">
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Product</label>
                <input type="text" className="form-input" value={productFilter}
                  onChange={e => setProductFilter(e.target.value.toUpperCase())}
                  placeholder="e.g. PL, HL, TWL..."
                  style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
              </div>
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>EWF State Code</label>
                <input type="text" className="form-input" value={stateFilter}
                  onChange={e => setStateFilter(e.target.value.toUpperCase())}
                  placeholder="ASSIGNED / CONTACTING / ..."
                  style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
              </div>
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>DPD min / max</label>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <input type="number" className="form-input" value={dpdMin} onChange={e => setDpdMin(e.target.value)} placeholder="min"
                    style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
                  <input type="number" className="form-input" value={dpdMax} onChange={e => setDpdMax(e.target.value)} placeholder="max"
                    style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
                </div>
              </div>
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Outstanding ₹ min / max</label>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <input type="number" className="form-input" value={outstandingMin} onChange={e => setOutstandingMin(e.target.value)} placeholder="min"
                    style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
                  <input type="number" className="form-input" value={outstandingMax} onChange={e => setOutstandingMax(e.target.value)} placeholder="max"
                    style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
                </div>
              </div>
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>EMI O/S Count min / max</label>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <input type="number" className="form-input" value={emiCountMin} onChange={e => setEmiCountMin(e.target.value)} placeholder="min"
                    style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
                  <input type="number" className="form-input" value={emiCountMax} onChange={e => setEmiCountMax(e.target.value)} placeholder="max"
                    style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
                </div>
              </div>
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>EMI O/S Amount ₹ min / max</label>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <input type="number" className="form-input" value={emiAmountMin} onChange={e => setEmiAmountMin(e.target.value)} placeholder="min"
                    style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
                  <input type="number" className="form-input" value={emiAmountMax} onChange={e => setEmiAmountMax(e.target.value)} placeholder="max"
                    style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
                </div>
              </div>
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Next Action Date (from / to)</label>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <input type="date" className="form-input" value={nextActionFrom} onChange={e => setNextActionFrom(e.target.value)}
                    style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
                  <input type="date" className="form-input" value={nextActionTo} onChange={e => setNextActionTo(e.target.value)}
                    style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
                </div>
              </div>
              <div className="filter-section">
                <SyutSelect
                  label="Next Action Type"
                  options={ACTION_TYPE_OPTIONS}
                  value={nextActionType}
                  onChange={(val) => setNextActionType((val as string) || '')}
                  isClearable={false}
                  size="sm"
                />
              </div>
              <div className="filter-section">
                <SyutSelect
                  label="Has Open Commitment"
                  options={[
                    { value: '',      label: 'Any' },
                    { value: 'true',  label: 'Yes' },
                    { value: 'false', label: 'No' },
                  ]}
                  value={hasOpenCommitment}
                  onChange={(val) => setHasOpenCommitment((val as string) || '')}
                  isClearable={false}
                  size="sm"
                />
              </div>
            </div>
            <div className="filter-actions">
              <button className="btn btn-secondary" onClick={clearFilters}>Clear</button>
              <button className="btn btn-primary" onClick={applyFilters}>Apply</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PreLitigationCasesScreen;
